import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "step", "stepIndicator", "previousButton", "nextButton", "submitButton", "form", "deviceSummary", "lostSummary", "userSummary", "reporterSummary", "fraudSummary" ]
  static values = { currentStep: Number, totalSteps: Number, confirmationStep: Boolean }

  connect() {
    // If totalSteps is not set, calculate it from the number of step elements
    if (!this.totalStepsValue || this.totalStepsValue === 0) {
      this.totalStepsValue = this.stepTargets.length
    }
    
    // Detect if this is a confirmation form by checking for summary targets
    this.hasConfirmationStep = this.hasDeviceSummaryTarget || this.hasLostSummaryTarget || this.hasUserSummaryTarget || this.hasReporterSummaryTarget || this.hasFraudSummaryTarget
    
    this.showCurrentStep()
    this.updateProgressBar()
  }

  nextStep(e) {
    e.preventDefault()
    
    // Validate current step
    if (!this.validateCurrentStep()) {
      return
    }

    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++
      this.showCurrentStep()
      this.updateProgressBar()
      
      // If we're on the confirmation step (last step with summary targets), populate the summary
      if (this.hasConfirmationStep && this.currentStepValue === this.totalStepsValue) {
        this.populateSummary()
      }
    }
  }

  previousStep(e) {
    e.preventDefault()
    
    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.showCurrentStep()
      this.updateProgressBar()
    }
  }

  showCurrentStep() {
    // Hide all steps
    this.stepTargets.forEach(step => {
      step.classList.add("hidden")
    })

    // Show current step
    const currentStep = this.stepTargets.find(step => 
      parseInt(step.dataset.step) === this.currentStepValue
    )
    if (currentStep) {
      currentStep.classList.remove("hidden")
    }

    // Update navigation buttons
    this.updateNavigationButtons()
  }

  updateNavigationButtons() {
    console.log(`Updating navigation - Step ${this.currentStepValue} of ${this.totalStepsValue}`)
    
    // Previous button
    if (this.currentStepValue > 1) {
      this.previousButtonTarget.classList.remove("hidden")
      // Also need to show the empty span for proper justify-between spacing
      this.previousButtonTarget.nextElementSibling?.classList.remove("hidden")
    } else {
      this.previousButtonTarget.classList.add("hidden")
      // Hide the empty span as well
      this.previousButtonTarget.nextElementSibling?.classList.add("hidden")
    }

    // Next/Submit buttons
    if (this.currentStepValue === this.totalStepsValue) {
      console.log("On final step - showing submit button")
      this.nextButtonTarget.classList.add("hidden")
      this.submitButtonTarget.classList.remove("hidden")
    } else {
      console.log("Not on final step - showing next button")
      this.nextButtonTarget.classList.remove("hidden")
      this.submitButtonTarget.classList.add("hidden")
    }
  }

  updateProgressBar() {
    this.stepIndicatorTargets.forEach((indicator, index) => {
      const stepNumber = index + 1
      const label = indicator.parentElement.querySelector('p')
      
      if (stepNumber <= this.currentStepValue) {
        indicator.classList.remove("bg-gray-300")
        indicator.classList.add("bg-primary")
        if (label) {
          label.classList.add("text-primary", "font-medium")
          label.classList.remove("text-gray-500")
        }
      } else {
        indicator.classList.add("bg-gray-300")
        indicator.classList.remove("bg-primary")
        if (label) {
          label.classList.remove("text-primary", "font-medium")
          label.classList.add("text-gray-500")
        }
      }

      // Update connecting lines
      const nextLine = indicator.parentElement.parentElement.querySelector('.h-1')
      if (nextLine) {
        if (stepNumber < this.currentStepValue) {
          nextLine.classList.remove("bg-gray-300")
          nextLine.classList.add("bg-primary")
        } else {
          nextLine.classList.add("bg-gray-300")
          nextLine.classList.remove("bg-primary")
        }
      }
    })
  }

  validateCurrentStep() {
    const currentStep = this.stepTargets.find(step => 
      parseInt(step.dataset.step) === this.currentStepValue
    )
    
    if (!currentStep) return true

    // Get all required fields in current step (check for data-required attribute)
    const requiredFields = currentStep.querySelectorAll('[data-required="true"]')
    let isValid = true

    requiredFields.forEach(field => {
      if (!field.value || field.value.trim() === '') {
        field.classList.add('border-red-500')
        isValid = false
      } else {
        field.classList.remove('border-red-500')
      }
    })

    if (!isValid) {
      alert('Please fill in all required fields')
    }

    return isValid
  }


  populateSummary() {
    const formData = new FormData(this.formTarget)
    console.log("Populating summary with:", formData)
    // Only populate device summary if the target exists
    if (this.hasDeviceSummaryTarget) {
      // Device Summary
      const requestType = this.getSelectText('device_request[request_action]')
      const purchaseReceipt = formData.get('device_request[purchase_receipt]')
      
      this.deviceSummaryTarget.innerHTML = `
      <div class="grid grid-cols-2 gap-y-2">
        <span class="text-gray-600">Request Type:</span>
        <span class="font-medium">${requestType}</span>
        
        <span class="text-gray-600">Primary IMEI:</span>
        <span class="font-medium">${formData.get('device_request[primary_imei]') || 'N/A'}</span>
        
        <span class="text-gray-600">Secondary IMEI:</span>
        <span class="font-medium">${formData.get('device_request[other_imei]') || 'N/A'}</span>
        
        <span class="text-gray-600">Device Model:</span>
        <span class="font-medium">${formData.get('device_request[device_model]') || 'N/A'}</span>
        
        <span class="text-gray-600">Marketing Name:</span>
        <span class="font-medium">${formData.get('device_request[marketing_name]') || 'N/A'}</span>
        
        <span class="text-gray-600">Device Type:</span>
        <span class="font-medium">${formData.get('device_request[device_type]') || 'N/A'}</span>
        
        <span class="text-gray-600">Device Branch:</span>
        <span class="font-medium">${formData.get('device_request[device_brand]') || 'N/A'}</span>
        
        <span class="text-gray-600">Primary Phone:</span>
        <span class="font-medium">${formData.get('device_request[primary_phone_number]') || 'N/A'}</span>
        
        <span class="text-gray-600">Secondary Phone:</span>
        <span class="font-medium">${formData.get('device_request[other_phone_number]') || 'N/A'}</span>
        
        <span class="text-gray-600">Purchase Receipt:</span>
        <span class="font-medium">${purchaseReceipt && purchaseReceipt.name ? purchaseReceipt.name : 'Not uploaded'}</span>
      </div>
    `
    }
    
    // Only populate lost summary if the target exists
    if (this.hasLostSummaryTarget) {
      // Lost Summary
      const policeReport = formData.get('device_request[police_report]')
      const lossDate = formData.get('device_request[loss_date]')
      const formattedDate = lossDate ? new Date(lossDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'
      
      this.lostSummaryTarget.innerHTML = `
      <div class="grid grid-cols-2 gap-y-2">
        <span class="text-gray-600">Date of Loss:</span>
        <span class="font-medium">${formattedDate}</span>
        
        <span class="text-gray-600">Location:</span>
        <span class="font-medium">${formData.get('device_request[loss_location]') || 'N/A'}</span>
        
        <span class="text-gray-600">Additional Info:</span>
        <span class="font-medium">${formData.get('device_request[additional_info]') || 'None provided'}</span>
      </div>
    `
    
    // Add police report info to the lost summary
    const policeReportRef = formData.get('device_request[police_report_reference]')
    if (policeReportRef || (policeReport && policeReport.name)) {
      this.lostSummaryTarget.innerHTML += `
        <div class="grid grid-cols-2 gap-y-2 mt-4 pt-4 border-t border-gray-200">
          <span class="text-gray-600">Police Report Ref:</span>
          <span class="font-medium">${policeReportRef || 'None'}</span>
          
          <span class="text-gray-600">Police Report:</span>
          <span class="font-medium">${policeReport && policeReport.name ? policeReport.name : 'Not uploaded'}</span>
        </div>
      `
      }
    }
    
    // Only populate user summary if the target exists
    if (this.hasUserSummaryTarget) {
      // User Information Summary
      const userName = formData.get('name') || this.formTarget.querySelector('input[name="name"]')?.value || 'N/A'
      const userEmail = formData.get('user_email') || this.formTarget.querySelector('input[name="user_email"]')?.value || 'N/A'
      const userPhone = formData.get('user_phone') || this.formTarget.querySelector('input[name="user_phone"]')?.value || 'N/A'
      const userIdType = formData.get('user_id_type') || this.formTarget.querySelector('input[name="user_id_type"]')?.value
      const userIdNumber = formData.get('user_id_number') || this.formTarget.querySelector('input[name="user_id_number"]')?.value
      
      let userSummaryHTML = `
      <div class="grid grid-cols-2 gap-y-2">
        <span class="text-gray-600">Name:</span>
        <span class="font-medium">${userName}</span>
        
        <span class="text-gray-600">Email:</span>
        <span class="font-medium">${userEmail}</span>
        
        <span class="text-gray-600">Phone:</span>
        <span class="font-medium">${userPhone}</span>
      `
      
      if (userIdType && userIdNumber) {
        userSummaryHTML += `
        <span class="text-gray-600">ID Type:</span>
        <span class="font-medium">${userIdType}</span>
        
        <span class="text-gray-600">ID Number:</span>
        <span class="font-medium">${userIdNumber}</span>
        `
      }
      
      userSummaryHTML += `</div>`
      
      this.userSummaryTarget.innerHTML = userSummaryHTML
    }
    
    // Only populate reporter summary if the target exists (for fraud reports)
    if (this.hasReporterSummaryTarget) {
      const reporterFirstName = formData.get('reporter_first_name') || this.formTarget.querySelector('input[name="reporter_first_name"]')?.value || 'N/A'
      const reporterLastName = formData.get('reporter_last_name') || this.formTarget.querySelector('input[name="reporter_last_name"]')?.value || 'N/A'
      const reporterEmail = formData.get('reporter_email') || this.formTarget.querySelector('input[name="reporter_email"]')?.value || 'N/A'
      const reporterPhone = formData.get('reporter_phone') || this.formTarget.querySelector('input[name="reporter_phone"]')?.value || 'N/A'
      
      this.reporterSummaryTarget.innerHTML = `
      <div class="grid grid-cols-2 gap-y-2">
        <span class="text-gray-600">Name:</span>
        <span class="font-medium">${reporterFirstName} ${reporterLastName}</span>
        
        <span class="text-gray-600">Email:</span>
        <span class="font-medium">${reporterEmail}</span>
        
        <span class="text-gray-600">Phone:</span>
        <span class="font-medium">${reporterPhone}</span>
      </div>
      `
    }
    
    // Only populate fraud summary if the target exists
    if (this.hasFraudSummaryTarget) {
      const fraudulentNumber = formData.get('fraud_report[mobile_number]') || 'N/A'
      const fraudType = this.getSelectText('fraud_report[fraud_type]')
      const fraudDescription = formData.get('fraud_report[fraud_description]') || 'N/A'
      const evidence = formData.get('fraud_report[evidence]')
      
      this.fraudSummaryTarget.innerHTML = `
      <div class="grid grid-cols-2 gap-y-2">
        <span class="text-gray-600">Fraudulent Number:</span>
        <span class="font-medium">${fraudulentNumber}</span>
        
        <span class="text-gray-600">Fraud Type:</span>
        <span class="font-medium">${fraudType}</span>
        
        <span class="text-gray-600">Evidence:</span>
        <span class="font-medium">${evidence && evidence.name ? evidence.name : 'Not uploaded'}</span>
      </div>
      <div class="mt-4">
        <span class="text-gray-600">Description:</span>
        <p class="font-medium mt-1">${fraudDescription}</p>
      </div>
      `
    }
  }

  getSelectText(name) {
    const select = this.formTarget.querySelector(`select[name="${name}"]`)
    if (!select) return 'N/A'
    return select.options[select.selectedIndex]?.text || 'N/A'
  }
}